import { ChartPart, TextPart } from "components/PageElements";
import React from "react";
import "./PageTemplate.css";
// DrawLayerを静的インポートに変更（動的インポートによるDOM変更を防ぐ）
import DrawLayer from "components/DrawLayer";

/**
 * 印刷用のCSSについては下記URLを参考にした
 * http://shiru-web.com/2017/11/07/01-76/amp/
 *
 */

/**
 * Template implementation
 * renderer, chartTypeResizeMap, chartStyle for each template pattern
 *
 * Current implementation is only for these two template patterns
 * - oneChart
 * - twoChart
 */

const PageTemplate = (props) => {
  const {
    params,
    changeHandler,
    templateId,
    renderer,
    chartTypeResizeMap,
    selectedChartId,
    portfolios,
    factorProps,
    amData,
    workspaceId,
    setTabIndex,
    setSelectedChartId,
    editable = false,
    tocIndex,
    pageIdIndexMap,
    pageIndex,
    printMode,
    productProps,
    productIdProps,
    assetNames,
    assetTypes,
    updateDate,
    isCanvasVisible = true,
  } = props;

  const {
    headerProps = {},
    titleProps = {},
    chart1Props = {},
    chart2Props = {},
    chart3Props = {},
    chart4Props = {},
    commentProps = {},
    wsProperties = {},
  } = params;
  const chartProps = {
    chart1: chart1Props,
    chart2: chart2Props,
    chart3: chart3Props,
    chart4: chart4Props,
  };
  const chartTypes = Object.entries(chartProps).reduce((acc, [key, value]) => {
    acc[key] = value?.chartType;
    return acc;
  }, {});

  const onSelectChart = (chartId, force) => {
    if (force) {
      setSelectedChartId(chartId);
    } else {
      const chart =
        !!selectedChartId && chartId === selectedChartId ? null : chartId;
      setSelectedChartId(chart);
    }
  };
  const onDblClickChart = (chartId) => {
    onSelectChart(chartId, true);
    setTabIndex(1);
  };
  const onHeaderChange = (e) => {
    e.preventDefault();
    const newHeaderText = e.target.value;
    const newParams = Object.assign(Object.assign(Object.assign({}, params)), {
      headerProps: { text: newHeaderText },
    });
    changeHandler(newParams);
  };
  const onTitleChange = (e) => {
    e.preventDefault();
    const newTitleText = e.target.value;
    const newParams = Object.assign(Object.assign(Object.assign({}, params)), {
      titleProps: { text: newTitleText },
    });
    changeHandler(newParams);
  };
  const onCommentChange = (e) => {
    e.preventDefault();
    const newCommentText = e.target.value;
    const newParams = Object.assign(Object.assign(Object.assign({}, params)), {
      commentProps: { text: newCommentText },
    });
    changeHandler(newParams);
  };

  const charts = {};
  const chartKeys = Object.keys(chartTypes);
  console.log("PageTemplate / assetnames", assetNames);
  chartKeys.forEach((chartKey) => {
    charts[chartKey] = (
      <ChartPart
        chartProps={chartProps[chartKey]}
        chartType={chartProps[chartKey].chartType}
        chartId={chartKey}
        chartTypeResizeMap={chartTypeResizeMap}
        templateId={templateId}
        portfolios={portfolios}
        factorProps={factorProps}
        amData={amData}
        workspaceId={workspaceId}
        selectHandler={onSelectChart}
        dblClickHandler={onDblClickChart}
        selected={selectedChartId === chartKey}
        editable={editable}
        productProps={productProps}
        productIdProps={productIdProps}
        assetNames={assetNames}
        assetTypes={assetTypes}
        updateDate={updateDate}
      />
    );
  });
  const header = (
    <TextPart
      {...headerProps}
      changeHandler={onHeaderChange}
      editable={editable}
    />
  );
  const title = (
    <TextPart
      {...titleProps}
      changeHandler={onTitleChange}
      editable={editable}
    />
  );
  const comment = (
    <TextPart
      {...commentProps}
      changeHandler={onCommentChange}
      editable={editable}
    />
  );

  const parameters = {
    header,
    title,
    comment,
    charts,
    wsProperties,
    tocIndex,
    pageIdIndexMap,
    pageIndex,
    printMode,
    drawData: params.drawData || [],
    onObjectCreated: props.onObjectCreated,
    onObjectSelected: props.onObjectSelected,
    onObjectDeselected: props.onObjectDeselected,
    isDrawingMode: props.isDrawingMode,
    showStylePanel: props.showStylePanel,
    editable,
  };

  console.log("PageTemplate rendered:", {
    templateId,
    editable,
    printMode,
    isDrawingMode: props.isDrawingMode,
    drawDataLength: (params.drawData || []).length,
    hasDrawData: !!(params.drawData && params.drawData.length > 0),
    hasTransformState: !!props.transformState,
  });

  // 印刷モード専用のデバッグログ
  if (printMode) {
    console.log("[PRINT_VIEW_DEBUG] PageTemplate in print mode:", {
      drawDataLength: (params.drawData || []).length,
      drawData: params.drawData,
      shouldShowDrawLayer: printMode && (params.drawData || []).length > 0,
      hasTransformState: !!props.transformState,
      transformState: props.transformState,
      editable,
      isPrintMode: printMode,
    });
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        // レイアウトに影響しないように厳密に制御
        margin: 0,
        padding: 0,
        border: "none",
        outline: "none",
        boxSizing: "border-box",
        // 子要素のレイアウトのみを制御し、親には影響しない
        contain: "layout style",
      }}
    >
      {/* ページコンテンツを描画 */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          zIndex: 1,
        }}
      >
        {React.createElement(renderer, parameters)}
      </div>

      {/* DrawLayerを正確にページサイズに合わせて配置（表示制御付き） */}
      {/* 編集モード: editable && isCanvasVisible の場合に表示 */}
      {/* 印刷/閲覧モード: printMode && drawData.length > 0 の場合に表示 */}
      {((editable && isCanvasVisible) ||
        (printMode && (params.drawData || []).length > 0)) && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
            pointerEvents: printMode ? "none" : "auto", // 印刷モードでは完全に非インタラクティブ
          }}
        >
          <DrawLayer
            drawData={params.drawData || []}
            onDrawDataChange={(newDrawData) => {
              // 印刷モードでは変更を無視
              if (printMode) {
                console.log(
                  "PageTemplate: onDrawDataChange ignored in print mode"
                );
                return;
              }
              console.log("PageTemplate: onDrawDataChange called", {
                newDrawDataLength: newDrawData.length,
                currentParamsKeys: Object.keys(params),
              });
              // paramsを完全に更新
              const newParams = {
                ...params,
                drawData: newDrawData,
              };
              changeHandler(newParams);
            }}
            onObjectCreated={props.onObjectCreated}
            onObjectSelected={props.onObjectSelected}
            onObjectDeselected={props.onObjectDeselected}
            editable={editable && !printMode} // 印刷モードでは編集不可
            isDrawingMode={props.isDrawingMode && !printMode} // 印刷モードでは描画不可
            drawingTool={props.drawingTool}
            showStylePanel={props.showStylePanel && !printMode} // 印刷モードではスタイルパネル非表示
            transformState={props.transformState}
            clearDrawSelectionRef={props.clearDrawSelectionRef}
            width="100%"
            height="100%"
            isPrintMode={printMode} // 印刷モードフラグを渡す
          />
        </div>
      )}
    </div>
  );
};

// DrawToolbarのテスト用コンポーネント
const DrawToolbarTest = () => {
  const [showStylePanel, setShowStylePanel] = React.useState(false);
  const [DrawToolbar, setDrawToolbar] = React.useState(null);

  React.useEffect(() => {
    import("components/DrawLayer/DrawToolbar")
      .then((module) => {
        setDrawToolbar(() => module.default);
      })
      .catch((error) => {
        console.error("DrawToolbar import failed", error);
      });
  }, []);

  return (
    <>
      {/* テスト用ボタン */}
      <div
        style={{
          position: "absolute",
          top: "50px",
          left: "50px",
          width: "150px",
          height: "50px",
          backgroundColor: "rgba(255, 0, 0, 0.3)",
          border: "1px solid red",
          pointerEvents: "auto",
          fontSize: "12px",
          color: "red",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          zIndex: 20,
        }}
        onClick={() => setShowStylePanel(!showStylePanel)}
      >
        {showStylePanel ? "Hide Panel" : "Show Panel"}
      </div>

      {/* DrawToolbarのみ表示 */}
      {DrawToolbar && (
        <DrawToolbar
          isVisible={showStylePanel}
          onStyleChange={() => {}}
          onDeleteSelected={() => {}}
          currentStyle={{
            strokeWidth: 2,
            stroke: "#000000",
            fill: "transparent",
            opacity: 1,
          }}
        />
      )}
    </>
  );
};

export default PageTemplate;
